// TENANT BRAND FOR GENERATED DOCUMENTS — DP64 Part 1c/1d.
//
// Chad's rule: in anything the tenant sends or prints there is no KEY-MADE name
// and no KEY-MADE logo, ever. The document carries the TENANT's logo, read from
// company_settings.logo_url. A tenant with no logo gets the words
// "Company Logo Here" in light type, in the same box, at the same size — which
// also prompts them to upload one.
//
// There is no fallback to any other mark in any branch, including the catch.
//
// DP99 pass 1 — this had its OWN copy of the settings, with its own cache, so a
// document tool asked the endpoint a second time. It now derives from the one
// held answer; the export shape is unchanged.
import { getCompanySettings } from '@/utils/companySettings';

const EMPTY = { companyName: '', logoUrl: '' };

export const loadTenantBrand = () =>
  getCompanySettings()
    .then((d) => ({
      companyName: (d || {}).company_name || '',
      logoUrl: (d || {}).logo_url || '',
    }))
    .catch(() => EMPTY);

// jsPDF cannot fetch a URL. A logo stored as a data URL goes straight in; a
// stored path is fetched and converted first.
const asDataUrl = async (url) => {
  if (!url) return null;
  if (url.startsWith('data:')) return url;
  try {
    const abs = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    const res = await fetch(abs);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result));
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
};

/**
 * Draw the tenant's logo inside the box (x, y, w, h), preserving aspect ratio.
 * Returns true if a logo drew, false if the placeholder words drew instead.
 */
export async function drawTenantLogo(pdf, { x, y, w, h, align = 'left', brand = null }) {
  const b = brand || (await loadTenantBrand());
  const dataUrl = await asDataUrl(b.logoUrl);

  if (dataUrl) {
    try {
      const props = pdf.getImageProperties(dataUrl);
      const scale = Math.min(w / props.width, h / props.height);
      const dw = props.width * scale;
      const dh = props.height * scale;
      const dx = align === 'center' ? x + (w - dw) / 2 : x;
      pdf.addImage(dataUrl, props.fileType || 'PNG', dx, y + (h - dh) / 2, dw, dh);
      return true;
    } catch (e) {
      // fall through to the words — never to another mark
    }
  }

  const prevSize = pdf.getFontSize();
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(Math.max(9, Math.min(14, h * 0.5)));
  pdf.setTextColor(180, 180, 180);
  const label = 'Company Logo Here';
  if (align === 'center') {
    pdf.text(label, x + w / 2, y + h / 2 + 1, { align: 'center' });
  } else {
    pdf.text(label, x, y + h / 2 + 1);
  }
  pdf.setFontSize(prevSize);
  pdf.setTextColor(0, 0, 0);
  return false;
}

export default { loadTenantBrand, drawTenantLogo };
